import { useEffect, useRef, useState } from "react";

import { ApiError, api, describeError } from "../api/client";
import type {
  Bucket,
  Citation,
  ConversationDetail,
  ConversationSummary,
  Feedback,
  Message,
} from "../api/types";
import { Markdown } from "../components/Markdown";
import { Provenance } from "../components/Provenance";
import "./chat.css";

interface Props {
  buckets: Bucket[];
  /** null = consulta nueva sin conversación creada aún. */
  conversationId: string | null;
  onConversationCreated: (conversation: ConversationSummary) => void;
  onListChanged: () => void;
  /** La conversación de la URL no existe: hay que dejar de apuntar a ella. */
  onConversationMissing: () => void;
  /**
   * Si ve las fuentes de cada respuesta. Solo la administración (decisión de Pamesa,
   * 22/10/2026). La API ya no las envía a nadie más; esto quita además el bloque de
   * fuentes y las marcas «[1]» del texto, que sin fuentes no remiten a nada.
   */
  showSources: boolean;
  /** Si puede abrir Colecciones: cambia lo que dice la bienvenida sin documentos. */
  canManage: boolean;
  /** Cambia con cada «+ Nueva consulta», aunque ya se esté en una consulta nueva. */
  resetToken: number;
}

/**
 * Las marcas de cita del texto, «[1]», «[2, 3]», «[1-4]». Mismo patrón que
 * `_MARKER` en `llm/generate.py`, que es quien las numera.
 */
const CITATION_MARKER = /\s?\[\d+(?:\s*[,-]\s*\d+)*\]/g;

function withoutMarkers(text: string): string {
  return text.replace(CITATION_MARKER, "");
}

/**
 * Same ceiling as `AskRequest.question` in the backend (`max_length=4000`).
 *
 * The composer had none, so a longer question was accepted by the form, the
 * conversation was created, and only then did the ask come back 422 — leaving an
 * empty conversation in the sidebar and an error the user could not act on,
 * because nothing said the question was too long.
 */
const QUESTION_MAX = 4000;

/** The answer while it arrives, before the server-owned copy replaces it. */
interface StreamingAnswer {
  content: string;
  citations: Citation[];
}

export function Chat({
  buckets,
  conversationId,
  onConversationCreated,
  onListChanged,
  onConversationMissing,
  showSources,
  canManage,
  resetToken,
}: Props) {
  const [active, setActive] = useState<ConversationDetail | null>(null);
  const [question, setQuestion] = useState("");
  const [scope, setScope] = useState<string>("all");
  const [asking, setAsking] = useState(false);
  const [streaming, setStreaming] = useState<StreamingAnswer | null>(null);
  const [error, setError] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const box = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (conversationId === null) {
      setActive(null);
      setError(null);
      return;
    }
    // The id can already match when the conversation was just created here.
    setActive((current) => {
      if (current?.id === conversationId) return current;
      api
        .conversation(conversationId)
        .then(setActive)
        .catch((e: unknown) => {
          // A 404 means the URL itself is wrong — a deleted conversation, a
          // pasted link, a typed id. Showing «No se ha encontrado.» and leaving
          // the address bar on the bad id left the view stuck: reloading, or
          // navigating away and back, returned to the same dead end. The URL is
          // repaired instead, and the error still gets shown on the way.
          if (e instanceof ApiError && e.status === 404) {
            onConversationMissing();
            return;
          }
          setError(describeError(e));
        });
      return current;
    });
  }, [conversationId, onConversationMissing]);

  // UNA CONSULTA NUEVA EMPIEZA EN «TODA LA DOCUMENTACIÓN». Antes se quedaba la
  // colección elegida en la conversación anterior, y la siguiente pregunta buscaba
  // solo ahí sin que nadie lo hubiera pedido. `resetToken` cubre el caso en que ya
  // se está en una consulta nueva y se vuelve a pulsar «+ Nueva consulta»: la URL no
  // cambia y `conversationId` sigue siendo null.
  useEffect(() => {
    if (conversationId === null) setScope("all");
  }, [conversationId, resetToken]);

  // AL VOLVER A UNA CONVERSACIÓN, SU COLECCIÓN. La API guarda el ámbito de la última
  // pregunta (`last_bucket_id`, null = toda la documentación). Si la colección ya no
  // existe, toda la documentación: un selector apuntando a algo borrado buscaría en
  // nada.
  const rememberedBucket = active?.last_bucket_id ?? null;
  useEffect(() => {
    if (active === null) return;
    const exists = buckets.some((bucket) => bucket.id === rememberedBucket);
    setScope(rememberedBucket && exists ? rememberedBucket : "all");
    // `active` entero no: cada respuesta lo sustituye, y esto debe correr solo cuando
    // cambia la conversación o la colección que recuerda.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.id, rememberedBucket, buckets]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [active?.messages.length, asking, streaming?.content.length]);

  /**
   * La caja crece con el texto hasta cuatro líneas, y a partir de ahí hace scroll.
   * El tope está en CSS (`max-height`) y no aquí: así el número de líneas se lee
   * donde están el resto de las medidas, y este efecto solo tiene que pedir el alto
   * del contenido. `height = "auto"` antes de medir es obligatorio — sin eso
   * `scrollHeight` devuelve el alto actual y la caja crece pero no vuelve a
   * encoger al borrar texto.
   */
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [question]);

  async function ask(event: React.FormEvent) {
    event.preventDefault();
    const text = question.trim();
    if (!text || asking) return;

    setAsking(true);
    setError(null);
    setQuestion("");

    let conversation = active;
    try {
      if (!conversation) {
        const created = await api.createConversation(scope === "all" ? null : scope);
        conversation = { ...created, messages: [] };
        setActive(conversation);
        onConversationCreated(created);
      }

      // Show the question immediately; the answer takes seconds.
      const pending: Message = {
        id: `pending-${Date.now()}`,
        role: "user",
        content: text,
        created_at: new Date().toISOString(),
        model: null,
        latency_ms: null,
        citations: [],
        // Una pregunta no se valora, solo las respuestas. El campo existe porque
        // el tipo es uno para los dos papeles.
        feedback: null,
      };
      setActive({ ...conversation, messages: [...conversation.messages, pending] });

      const bucketIds = scope === "all" ? [] : [scope];
      await api.askStream(conversation.id, text, bucketIds, {
        onCitations: (citations) =>
          setStreaming((current) => ({ content: current?.content ?? "", citations })),
        onDelta: (delta) =>
          setStreaming((current) => ({
            content: (current?.content ?? "") + delta,
            citations: current?.citations ?? [],
          })),
        // Arrives after the last delta, so the sources split as soon as the answer
        // finishes instead of waiting for the re-read below.
        onCited: (positions) =>
          setStreaming((current) =>
            current
              ? {
                  content: current.content,
                  citations: current.citations.map((citation, index) => ({
                    ...citation,
                    cited: positions.includes(index + 1),
                  })),
                }
              : current,
          ),
      });

      // Re-read rather than keep the streamed copy: the server owns message ids,
      // ordering and the stored citations.
      setActive(await api.conversation(conversation.id));
      onListChanged();
    } catch (e: unknown) {
      setError(describeError(e));
      setQuestion(text);
      // The question may have been stored before the stream died; re-read so
      // what is shown matches what the server kept.
      if (conversation) {
        const fallback = conversation;
        setActive(await api.conversation(fallback.id).catch(() => fallback));
        onListChanged();
      }
    } finally {
      setStreaming(null);
      setAsking(false);
    }
  }

  const scopeBucket = buckets.find((bucket) => bucket.id === scope) ?? null;

  return (
    <div className="chat">
      <header className="chat-head">
        <h1 className="chat-head-title">Chat</h1>
        <label className={`scope${scopeBucket ? " scope--bucket" : ""}`}>
          <span className="scope-dot" aria-hidden="true" />
          <select
            value={scope}
            onChange={(event) => setScope(event.target.value)}
            disabled={asking}
            aria-label="Colección en la que buscar"
          >
            <option value="all">Toda la documentación</option>
            {buckets.map((bucket) => (
              <option key={bucket.id} value={bucket.id}>
                {bucket.name} ({bucket.document_count})
              </option>
            ))}
          </select>
        </label>
      </header>

      <div className="chat-messages">
        {active === null && !asking ? (
          <Welcome buckets={buckets} showSources={showSources} canManage={canManage} />
        ) : (
          <>
            {active?.messages.map((message) => (
              <Bubble
                key={message.id}
                message={message}
                conversationId={active?.id ?? null}
                showSources={showSources}
              />
            ))}
            {streaming && streaming.content && (
              <article className="msg msg--assistant" aria-live="polite">
                <Markdown>
                  {showSources ? streaming.content : withoutMarkers(streaming.content)}
                </Markdown>
                {showSources && <Provenance citations={streaming.citations} />}
              </article>
            )}
            {asking && !streaming?.content && <Thinking />}
          </>
        )}
        <div ref={endRef} />
      </div>

      {error && (
        <p className="chat-error" role="alert">
          {error}
        </p>
      )}

      <form className="composer" onSubmit={ask}>
        <textarea
          ref={box}
          value={question}
          onChange={(event) => setQuestion(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              void ask(event);
            }
          }}
          placeholder="Pregunta sobre series, acabados, formatos…"
          maxLength={QUESTION_MAX}
          rows={1}
          disabled={asking}
          aria-label="Tu pregunta"
        />
        <button
          type="submit"
          className="composer-send"
          disabled={asking || !question.trim()}
          aria-label={asking ? "Consultando…" : "Enviar"}
        >
          →
        </button>
      </form>
    </div>
  );
}

function Bubble({
  message,
  conversationId,
  showSources,
}: {
  message: Message;
  conversationId: string | null;
  showSources: boolean;
}) {
  if (message.role === "user") {
    return (
      <article className="msg msg--user">
        <p className="msg-text">{message.content}</p>
      </article>
    );
  }

  return (
    <article className="msg msg--assistant">
      <Markdown>{showSources ? message.content : withoutMarkers(message.content)}</Markdown>
      {showSources && <Provenance citations={message.citations} />}
      <div className="msg-foot">
        {message.model && (
          <p className="msg-meta">
            {message.model}
            {message.latency_ms !== null && ` · ${(message.latency_ms / 1000).toFixed(1)} s`}
          </p>
        )}
        {conversationId && <Rating conversationId={conversationId} message={message} />}
      </div>
    </article>
  );
}

/**
 * Thumbs on an answer, and a comment when the thumb is down.
 *
 * Not asked for on a positive rating, deliberately: the previous system did the
 * same, and a box that appears on every click makes people stop clicking. What is
 * worth reading is why an answer was wrong.
 *
 * Optimistic, and reverted on failure. A rating is not worth a spinner, but it is
 * worth not lying about: showing a filled thumb for a request that never landed
 * would quietly lose the signal this exists to collect.
 */
function Rating({
  conversationId,
  message,
}: {
  conversationId: string;
  message: Message;
}) {
  const [rating, setRating] = useState<Feedback | null>(message.feedback);
  const [comment, setComment] = useState(message.feedback?.comment ?? "");
  const [writing, setWriting] = useState(false);
  const [failed, setFailed] = useState(false);

  async function send(isPositive: boolean, text: string) {
    const previous = rating;
    setRating({ is_positive: isPositive, comment: text });
    setFailed(false);
    try {
      await api.rateAnswer(conversationId, message.id, isPositive, text);
    } catch {
      setRating(previous);
      setFailed(true);
    }
  }

  return (
    <div className="rating">
      {/* Solo el negativo. Una respuesta correcta es lo normal y no hace falta
          decirlo; lo que hay que poder marcar sin fricción es la que falla, que es
          la que alguien tiene que mirar. */}
      <button
        type="button"
        className={`rating-thumb rating-thumb--mal${rating?.is_positive === false ? " is-on" : ""}`}
        aria-pressed={rating?.is_positive === false}
        aria-label="La respuesta no es correcta"
        title="La respuesta no es correcta"
        onClick={() => {
          setWriting(true);
          void send(false, comment);
        }}
      />

      {writing && (
        <form
          className="rating-note"
          onSubmit={(event) => {
            event.preventDefault();
            setWriting(false);
            void send(false, comment);
          }}
        >
          <label className="rating-label" htmlFor={`nota-${message.id}`}>
            ¿Qué falla? Opcional
          </label>
          <textarea
            id={`nota-${message.id}`}
            className="rating-text"
            value={comment}
            maxLength={4000}
            rows={2}
            onChange={(event) => setComment(event.target.value)}
            placeholder="Se ha inventado el dato, falta la fuente, responde otra cosa…"
          />
          <button type="submit" className="rating-save">
            Guardar
          </button>
        </form>
      )}

      {failed && (
        <p className="rating-failed" role="status">
          No se ha podido guardar la valoración.
        </p>
      )}
    </div>
  );
}

function Thinking() {
  return (
    <article className="msg msg--assistant" aria-live="polite">
      <p className="msg-thinking">
        Buscando en la documentación
        <span className="dots" aria-hidden="true">
          <i />
          <i />
          <i />
        </span>
      </p>
    </article>
  );
}

function Welcome({
  buckets,
  showSources,
  canManage,
}: {
  buckets: Bucket[];
  showSources: boolean;
  canManage: boolean;
}) {
  const documents = buckets.reduce((total, bucket) => total + bucket.document_count, 0);
  return (
    <article className="msg msg--assistant welcome">
      <p className="welcome-body">
        Hola. Pregunta lo que necesites sobre la documentación
        {showSources
          ? ": cada respuesta cita los documentos en los que se apoya, con la página exacta, para que puedas comprobarla."
          : "."}{" "}
        Ahora mismo hay {documents} documento{documents === 1 ? "" : "s"} en{" "}
        {buckets.length} colec{buckets.length === 1 ? "ción" : "ciones"}.
      </p>
      {documents === 0 && (
        <p className="welcome-empty">
          {canManage
            ? "Todavía no hay documentos indexados. Súbelos en Colecciones y vuelve a preguntar."
            : "Todavía no hay documentos indexados. Avisa a quien gestiona las colecciones."}
        </p>
      )}
    </article>
  );
}

